'use client'

import { CouponCallout, type CouponCalloutText } from '@/components/client/shared/coupon-callout'
import { useTranslation } from '@/hooks/client/use-translation'

import type { PriceSellerData } from './price-section-types'

interface SellerCtaProps {
  seller: PriceSellerData
  releaseSlug: string
  couponText: CouponCalloutText
}

const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3.5 w-3.5 shrink-0">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"
    />
    <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
  </svg>
)

/**
 * The seller row's action block. The CTA carries exactly the tracking
 * attributes the control's card does — same event, position, and test name —
 * so click-outs from both arms land in one funnel and the A/B compares like
 * with like. Only `segment` (stamped on every event) tells the arms apart.
 */
export const SellerCta = ({ seller, releaseSlug, couponText }: SellerCtaProps) => {
  const { t } = useTranslation()
  const learnMore = t('product.purchase.viewOffer', 'View offer ↗')
  if (!seller.url && !(seller.coupon && seller.couponLabel)) {
    return null
  }

  return (
    <div className="flex w-full flex-col items-center gap-2 sm:w-auto sm:justify-self-end">
      {seller.url && (
        <a
          href={seller.url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="bg-lapis hover:bg-navy flex h-11 w-full min-w-[120px] sm:w-[120px] items-center justify-center rounded-xl text-[14px] font-bold text-white shadow-sm transition-all duration-150 hover:shadow-md active:scale-[0.97]"
          data-track
          data-track-impression
          data-event-name="AffiliateAction"
          data-event-seller={seller.sellerName}
          data-affiliate-seller-type={seller.sellerType}
          data-event-product={releaseSlug}
          data-event-position="product-prices"
          data-affiliate-cta={learnMore}
          data-affiliate-text={learnMore}
          data-affiliate-test-name="product-prices"
          data-event-url={seller.url}
        >
          {learnMore}
        </a>
      )}
      {seller.coupon && seller.couponLabel && (
        <CouponCallout
          coupon={seller.coupon}
          affiliateUrl={seller.url ?? undefined}
          sellerName={seller.sellerName}
          tracking={{
            position: 'product-prices-coupon',
            product: releaseSlug,
            seller: seller.sellerName,
            sellerType: seller.sellerType,
          }}
          label={seller.couponLabel}
          text={couponText}
        >
          <TagIcon />
          {seller.couponLabel}
        </CouponCallout>
      )}
    </div>
  )
}
